import React, { useLayoutEffect, useRef } from "react";

import "../CSS/Components/TeamShowcase.css";

import JosephPortrait from "../Images/team-extract/team-extract-3.jpg";
import TomerPortrait from "../Images/team-extract/team-extract-4.jpg";
import BlessedPortrait from "../Images/team-extract/dscf1899-1-1.webp";
import AlexeyPortrait from "../Images/team-extract/team-extract-5.png";

export const teamShowcaseMembers = [
  {
    id: "joseph-rosales",
    name: "Joseph Rosales",
    role: "Creative Director",
    image: JosephPortrait,
    imageAlt: "Joseph Rosales portrait",
    imagePosition: "50% 18%",
    description: (
      <>
        <span className="team-showcase__paragraph">
          <><span className="team-showcase__highlight">A multidisciplinary creative</span> translating artistic concepts into tangible visual products that engages across music, film and fashion,  </>
          <span className="team-showcase__highlight">building immersive worlds that reflect the heart of a brand</span>.
        </span>
      </>
    ),
  },
  {
    id: "tomer-keysar",
    name: "Tomer Keysar",
    role: "Director of Photography",
    image: TomerPortrait,
    imageAlt: "Tomer Keysar portrait",
    imagePosition: "50% 34%",
    description: (
      <>
        With clients like <span className="team-showcase__highlight">VERSACE, NBCUniversal, and VAVS</span>, <>Tomer brings
        high-end commercial experience and a cinematic eye to every project, elevating even grassroots campaigns to </>
        <span className="team-showcase__highlight"> industry-level quality</span>.
      </>
    ),
  },
  {
    id: "blessed-barrios",
    name: "Blessed Barrios",
    role: "Cultural Coordinator",
    image: BlessedPortrait,
    imageAlt: "Blessed Barrios portrait",
    imagePosition: "50% 42%",
    description: (
      <>
         <span className="team-showcase__highlight"> Managing 50-person teams</span> and <span className="team-showcase__highlight">fashion shows with over 200</span> in attendance, Blessed carefully <span className="team-showcase__highlight">curates programs that build consumer loyalty</span> with brands by blending culture and creativity.
      </>
    ),
  },
  {
    id: "alexey-casual",
    name: "Alexey Casual",
    role: "Editor/VFX Specialist",
    image: AlexeyPortrait,
    imageAlt: "Alexey Casual portrait",
    imagePosition: "50% 24%",
    description: (
      <>
        Alexey&apos;s work has shaped visuals for <span className="team-showcase__highlight">Lil Yachty</span> <>and the </>
        <span className="team-showcase__highlight">CONCRETE BOYS</span>, and <span className="team-showcase__highlight">Netflix</span> projects to bring stylized, emotionally driven
        finishing touches that <span className="team-showcase__highlight">define modern visual branding</span>.
      </>
    ),
  },
];

export const teamShowcaseMemberCount = teamShowcaseMembers.length;

const teamShowcaseHomeSlots = ["top", "middle", "bottom"];

const renderDescriptionStack = (normalizedActiveIndex) => (
  <div className="team-showcase__description-stage secondaryBody" aria-live="polite">
    {teamShowcaseMembers.map((member, memberIndex) => (
      <div
        key={member.id}
        className={`team-showcase__description ${memberIndex === normalizedActiveIndex ? "is-active" : "is-inactive"}`}
        aria-hidden={memberIndex !== normalizedActiveIndex}
      >
        {member.description}
      </div>
    ))}
  </div>
);

function MobileDescriptionStack({ normalizedActiveIndex }) {
  const stageRef = useRef(null);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const activeDescription = stage?.querySelector(".team-showcase__description.is-active");

    if (!stage || !activeDescription) return undefined;

    const updateHeight = () => {
      const stageStyle = window.getComputedStyle(stage);
      const verticalPadding = parseFloat(stageStyle.paddingTop) + parseFloat(stageStyle.paddingBottom);
      stage.style.height = `${activeDescription.scrollHeight + verticalPadding}px`;
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(activeDescription);

    return () => resizeObserver.disconnect();
  }, [normalizedActiveIndex]);

  return (
    <div ref={stageRef} className="team-showcase__description-stage secondaryBody" aria-live="polite">
      {teamShowcaseMembers.map((member, memberIndex) => (
        <div
          key={member.id}
          className={`team-showcase__description ${memberIndex === normalizedActiveIndex ? "is-active" : "is-inactive"}`}
          aria-hidden={memberIndex !== normalizedActiveIndex}
        >
          {member.description}
        </div>
      ))}
    </div>
  );
}

const renderMemberCards = (normalizedActiveIndex) => (
  teamShowcaseMembers.map((member, memberIndex) => {
    const isActiveMember = memberIndex === normalizedActiveIndex;
    const slotName = isActiveMember ? "primary" : "placeholder";
    const mobileSlotIndex = memberIndex < normalizedActiveIndex ? memberIndex : memberIndex - 1;
    const homeSlot = isActiveMember ? teamShowcaseHomeSlots[0] : teamShowcaseHomeSlots[mobileSlotIndex];
    const mobileStyle = isActiveMember ? undefined : {
      "--team-mobile-slot": mobileSlotIndex,
      "--team-mobile-slot-count": teamShowcaseMembers.length - 1,
    };

    return (
      <article
        key={member.id}
        data-member-id={member.id}
        className={`team-showcase__member team-showcase__member--${slotName} team-showcase__member--home-${homeSlot} ${isActiveMember ? "team-showcase__member--primary" : "team-showcase__member--inactive"}`}
        aria-current={isActiveMember ? "true" : "false"}
        style={mobileStyle}
      >
        <div className="team-showcase__photo-frame">
          <img
            src={member.image}
            alt={member.imageAlt}
            className="team-showcase__photo"
            loading="lazy"
            decoding="async"
            style={{ objectPosition: member.imagePosition }}
          />
        </div>

        <div className="team-showcase__member-copy">
          <h3 className="team-showcase__member-name importantBody">{member.name}</h3>
          <p className="team-showcase__member-role quote">{member.role}</p>
          <div className="team-showcase__member-description">{member.description}</div>
        </div>
      </article>
    );
  })
);

function DesktopTeamShowcase({ normalizedActiveIndex, direction }) {
  return (
    <div className={`team-showcase team-showcase--desktop ${direction < 0 ? "team-showcase--backward" : "team-showcase--forward"}`}>
      <div className="team-showcase__grain" aria-hidden="true" />
      <div className="team-showcase__desktop-title-plate" aria-hidden="true" />
      <div className="team-showcase__desktop-right-rail" aria-hidden="true" />

      <div className="team-showcase__headline importantBody">
        <span className="team-showcase__headline-copy">OUR</span>
        <span className="team-showcase__headline-copy team-showcase__headline-copy--accent">TEAM</span>
      </div>

      <div className="team-showcase__badge quote" aria-hidden="true">
        <span className="team-showcase__badge-line">WE CREATED ALL</span>
        <span className="team-showcase__badge-line team-showcase__badge-line--light">IMAGES ON THIS</span>
        <span className="team-showcase__badge-line team-showcase__badge-line--light">WEBSITE</span>
      </div>

      {renderDescriptionStack(normalizedActiveIndex)}
      {renderMemberCards(normalizedActiveIndex)}
    </div>
  );
}

function MobileTeamShowcase({ normalizedActiveIndex, direction }) {
  return (
    <div className={`team-showcase team-showcase--mobile ${direction < 0 ? "team-showcase--backward" : "team-showcase--forward"}`}>
      <div className="team-showcase__grain" aria-hidden="true" />

      <div className="team-showcase__headline importantBody">
        <span className="team-showcase__headline-copy">OUR</span>
        <span className="team-showcase__headline-copy team-showcase__headline-copy--accent">TEAM</span>
      </div>

      <div className="team-showcase__badge quote" aria-hidden="true">
        <span className="team-showcase__badge-line">WE CREATED ALL</span>
        <span className="team-showcase__badge-line team-showcase__badge-line--light">IMAGES ON THIS</span>
        <span className="team-showcase__badge-line team-showcase__badge-line--light">WEBSITE</span>
      </div>

      <MobileDescriptionStack normalizedActiveIndex={normalizedActiveIndex} />
      {renderMemberCards(normalizedActiveIndex)}

      <div className="team-showcase__scroll-indicator fineBody">Scroll to swap team members</div>
    </div>
  );
}

function TeamShowcase({ activeIndex = 0, direction = 1, isPhoneLayout = false }) {
  const normalizedActiveIndex = ((activeIndex % teamShowcaseMembers.length) + teamShowcaseMembers.length) % teamShowcaseMembers.length;

  return isPhoneLayout
    ? <MobileTeamShowcase normalizedActiveIndex={normalizedActiveIndex} direction={direction} />
    : <DesktopTeamShowcase normalizedActiveIndex={normalizedActiveIndex} direction={direction} />;
}

export default TeamShowcase;